import { onLoaded, isUtagAutoLoadEnabled, loadUtagScript, appendUtagData } from '@academysports/utils-lib/src/tealium/tealium';
import { initializeInpTracking } from '@academysports/utils-lib/src/inp/inp';
import AxiosInterceptor from '../utils/interceptor';
import ConfigureStore from './configureStore';
import '../assets/scss/vendor.scss';
import StorageManager from '../utils/StorageManager';
import attachHydrationIntersectionObserver from '../utils/hydrationDeferrer';
// import '../utils/analytics/analyticsOnScroll';
import * as ReactFile from './includes/react';
import * as ReduxFile from './includes/redux';
import * as UtilsFile from './includes/utils';
import managePersistentSessions from './customGlobalOperations';
import { getUserMetadata } from '../utils/tealiumUtils';

/**
 * Complete the init of tealium utag_data fields for the current user. (The page-specific
 * metadata fields were defined server-side, but akamai page response caches means anything
 * involving user cookies cannot be done there.) This MAY also auto-load the utag.js file
 * unless a control flag is present.
 * @returns - undefined / nothing
 */
const initializeTealium = () => {
  /* IMPORTANT:
   * Due to page response caching effects, the metadata fields for the user must be computed
   * client-side. any initial values from a server render are probably all 'not_set'.
   */
  const userMeta = getUserMetadata();
  appendUtagData(userMeta);
  /* In the CMS for a page, we may elect to skip auto-loading of the utag.js file.
   * In that case, your local component/app code will have to manually load it in.
   * See the oden codebase for 'deferUtagScriptLoad' (and CMS 'defer_tealium_load')
   */
  if (isUtagAutoLoadEnabled()) {
    loadUtagScript(onLoaded);
  }
};

/**
 * Function to inject common data authored by CMS into all the component before store initialization.
 * @param {Object} asoData The window.ASOData containing individual component data.
 */
const commonDataInjector = asoData => {
  const isNodeCompPropsCachingEnabled = asoData?.uiConfig?.cmsGlobal?.isNodeCompPropsCachingEnabled;

  Object.keys(asoData).forEach(key => {
    if (asoData[key] && asoData[key].cms) {
      /* eslint-disable no-param-reassign */
      // Necessary to prevent same data being injected by oden into script tags increasing page size.
      ['labels', 'messages', 'uiConfig'].forEach(n => {
        asoData[key][n] = { ...asoData[key][n], ...asoData[n] };
      });

      if (isNodeCompPropsCachingEnabled) {
        const componentPropsToBeCached = asoData?.uiConfig?.cmsGlobal?.componentPropsToBeCached;
        const cachedComponentPropsList = componentPropsToBeCached?.split(',');
        if (Array.isArray(cachedComponentPropsList) && componentPropsToBeCached.indexOf(asoData[key].rcn) !== -1) {
          asoData[key].pageInfo = { ...asoData[key].pageInfo, ...asoData.pageInfo };
          asoData[key].requestUrl = asoData?.requestUrl;
          asoData[key].asoSsrWidth = asoData?.asoSsrWidth;
          asoData[key].uiConfig = asoData?.uiConfig;
        }
      }
      /* eslint-enable no-param-reassign */
    }
  });
  // Get data-components on the page and push the comp ID based data to window initial state
  // TBD: Unique ID based push
  [...document.querySelectorAll('[data-component]')].forEach(componentNode => {
    // Get componentNode.dataset.compid & componentNode.dataset.component
    const compId = componentNode.getAttribute('data-compId');
    const compName = componentNode.getAttribute('data-component');
    if (compId && compName) {
      if (asoData[compId]) {
        window.INITIAL_STATE = window.INITIAL_STATE || {};
        window.INITIAL_STATE[compName] = asoData[compId].api || {};
        // TBD: move to component name to unique component ID
        // window.INITIAL_STATE[compId] = asoData[compId].api || {};
        // Adds server generated styles to emotion cache.
        // const ids = asoData[compId].emotion;
        // if (window.EmotionCSS && ids) {
        //   console.log('##### ids', compName, ids, '123');
        //   window.EmotionCSS.hydrate(ids);
        //   hydrate(ids);
        // }
      }
    }
  });
  // Check if there exists persisted data from the session storage and assign it to the inital data.
  const persistedObject = JSON.parse(StorageManager.getSessionStorage(StorageManager.getCookie('JSESSIONID')));
  if (persistedObject && typeof persistedObject === 'object' && !Array.isArray(persistedObject)) {
    Object.keys(persistedObject).forEach(key => {
      window.INITIAL_STATE[key] = persistedObject[key];
    });
  }
};

/**
 * Function to get the query parameter from the URL.
 * NOTE: Not tested with complex Query params, only used for PWA check
 * @param {string} key The query param to find
 */
const getQueryStringValue = key =>
  decodeURIComponent(
    // eslint-disable-next-line no-useless-escape
    window.location.search.replace(new RegExp(`^(?:.*[&\\?]${encodeURIComponent(key).replace(/[\.\+\*]/g, '\\$&')}(?:\\=([^&]*))?)?.*$`, 'i'), '$1')
  );

const intializeVendor = () => {
  // Attach intersectionobserver for deferring component hydration
  attachHydrationIntersectionObserver();

  const vendorModules = { ...ReactFile, ...ReduxFile, ...UtilsFile };

  // Assign modules to global window object
  Object.keys(vendorModules).forEach(key => {
    if (vendorModules[key]) {
      window[key] = window[key] || vendorModules[key];
    }
  });
  // Inject common data into window.ASOData[<component-ids>]
  commonDataInjector(window.ASOData);
  // Local variables for data interchange
  const { axios, Redux, ReduxSaga } = vendorModules;
  const interceptor = new AxiosInterceptor(axios);
  interceptor.initialize();
  /* Store is created by hydrating with the initialstate recevied fron node.
   */
  window.INITIAL_STATE = window.INITIAL_STATE || {};
  window.store = ConfigureStore(Redux, ReduxSaga)(window.INITIAL_STATE);

  initializeTealium(); // INITIATE TEALIUM SCRIPT
  initializeInpTracking();

  if ('serviceWorker' in navigator && getQueryStringValue('sw') === 'true') {
    window.addEventListener('load', () => {
      const { staticVendorBucketPath, staticAssetsBucketPath } = window.ASOPMEnv;
      const { buildVersion } = window.ASOData;
      const isLocalhost = window.location.host.includes('localhost');
      const getStaticAssetPath = isLocal => {
        let finalAssetPath = '';
        if (isLocal) {
          finalAssetPath = `${staticAssetsBucketPath}/&buildVersion=${buildVersion}`;
        } else {
          finalAssetPath = `${window.location.origin}${staticAssetsBucketPath}/&buildVersion=${buildVersion}`;
        }
        return finalAssetPath;
      };

      const assetPath = isLocalhost ? staticAssetsBucketPath : staticVendorBucketPath;
      const swUrl = isLocalhost ? '/cr/vendor/sw.js' : `${assetPath}sw.js`;
      const finalSwUrl = `${swUrl}?staticPath=${getStaticAssetPath(isLocalhost)}`;

      console.log('SW URL', swUrl);

      navigator.serviceWorker
        .register(finalSwUrl)
        .then(registration => {
          console.log('SW registered successfully', registration);
        })
        .catch(registrationError => {
          console.log('SW registration failed: ', registrationError);
        });
    });
  } else {
    console.log('service worker is not supported');
  }

  // Trigger Custom global JS configurations

  // Remove Persistence Data if Persistent Cookie is dropped from Akamai - OMNI-75712
  managePersistentSessions();

  // Custom code ends
};
intializeVendor();
